import {
  KAKI_MAKAN_EVENT_CATEGORY,
  KakiMakanSubSectionListMapping,
} from "@config/kakiMakan";
import { getKeyByValue } from "@helper/utils";
import { CD_LANGUAGE } from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import { useCallback, useState } from "react";

import { KakiMakanSubSectionListProps } from "../KakiMakanSubSectionList";

type useKakiMakanSubSectionListData = {
  expanded: boolean;
  selectedSection: string | null;
  handleSelect: (item: string) => void;
  handleToggle: () => void;
};

type useKakiMakanSubSectionListProps = KakiMakanSubSectionListProps & {
  bhCustomEvent: ReturnType<
    (typeof import("@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall"))["useBHCustomEventAnalyticsCall"]
  >;
};

/**
 * Custom React Hook: useKakiMakanSubSectionListHook
 * Description: [callbacks for toggle and selection]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useKakiMakanSubSectionList({
  bhCustomEvent,
  onSelect,
  initialExpanded = false,
  initialSelection = null,
}: useKakiMakanSubSectionListProps): useKakiMakanSubSectionListData {
  const [expanded, setExpanded] = useState(initialExpanded);
  const [selectedSection, setSelectedSection] = useState<string | null>(
    initialSelection,
  );

  const handleSelect = useCallback(
    (item: string) => {
      const newSelection = selectedSection === item ? null : item;
      setSelectedSection(newSelection);
      onSelect(newSelection); // only triggers on click
      newSelection &&
        bhCustomEvent(
          KAKI_MAKAN_EVENT_CATEGORY,
          "search",
          getKeyByValue(KakiMakanSubSectionListMapping, newSelection),
        );
    },
    [selectedSection, onSelect, bhCustomEvent],
  );

  const handleToggle = useCallback(() => {
    bhCustomEvent(
      KAKI_MAKAN_EVENT_CATEGORY,
      "search",
      expanded ? "view less" : "view more",
      undefined,
      CD_LANGUAGE.ENGLISH,
    );
    setExpanded((prev) => !prev);
  }, [bhCustomEvent, expanded]);

  return {
    expanded,
    selectedSection,
    handleSelect,
    handleToggle,
  };
}
